import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getApiErrorMessage, useApiGet } from '../../../utils/httpClientTs';
import { getPortalLoanHome } from '../../../endpoints/portalLoans';
import type { PortalPayConfirmState } from '../../../types/portalLoans';
import { formatPortalCurrency } from '../mock/store';
import { ELF_TEAL, elfPrimaryButtonSx } from '../elfTheme';

const LoanPay: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useApiGet(
    ['portal-loans-me'],
    getPortalLoanHome,
    { retry: 1 }
  );

  const installment =
    data?.payment_info.next_payment_amount != null &&
    data.payment_info.next_payment_amount > 0
      ? data.payment_info.next_payment_amount
      : null;

  const [amountMode, setAmountMode] = useState<'installment' | 'custom'>(
    'installment'
  );
  const [customAmount, setCustomAmount] = useState('');

  useEffect(() => {
    if (installment != null) {
      setCustomAmount(String(installment));
      setAmountMode('installment');
    } else if (data) {
      setAmountMode('custom');
    }
  }, [data, installment]);

  const amount =
    amountMode === 'installment' && installment != null
      ? installment
      : Number.parseFloat(customAmount) || 0;

  const handleContinue = () => {
    if (amount <= 0) return;
    const state: PortalPayConfirmState = {
      amount,
      amountMode:
        amountMode === 'installment' && installment != null
          ? 'installment'
          : 'custom',
    };
    navigate('/portal/loans/pay/confirm', { state });
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ELF_TEAL }} />
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error) || 'Unable to load your loan account.'}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Make a payment
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Pay by card or bank account. Funds post to your ELF account when the
        payment is approved.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <FormControl sx={{ mb: 3, display: 'block' }}>
          <FormLabel>Payment amount</FormLabel>
          <RadioGroup
            value={amountMode}
            onChange={(_, v) => setAmountMode(v as 'installment' | 'custom')}
          >
            <FormControlLabel
              value="installment"
              control={<Radio />}
              disabled={installment == null}
              label={
                installment != null
                  ? `Scheduled installment (${formatPortalCurrency(installment)})`
                  : 'Scheduled installment (not available)'
              }
            />
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Custom amount"
            />
          </RadioGroup>
          {amountMode === 'custom' && (
            <TextField
              label="Amount"
              type="number"
              size="small"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              inputProps={{ min: 0.01, step: '0.01' }}
              sx={{ mt: 1, maxWidth: 200 }}
            />
          )}
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          On the next step you can pay with a card or a bank account.
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={amount <= 0}
            sx={elfPrimaryButtonSx}
          >
            Continue
          </Button>
          <Button onClick={() => navigate('/portal/loans')}>Cancel</Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoanPay;
